import { config } from './env';

const SCRIPT_ID = 'recaptcha-v3';

interface GrecaptchaApi {
  ready: (callback: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
}

declare global {
  interface Window {
    grecaptcha?: GrecaptchaApi;
  }
}

let loader: Promise<GrecaptchaApi> | null = null;

/**
 * True when the challenge can actually run: switched on AND holding a site key. Either one missing means
 * the portal runs with verification unavailable, and says so on screen rather than looking protected.
 */
export function isRecaptchaActive(): boolean {
  return config.recaptchaEnabled && config.recaptchaSiteKey.length > 0;
}

function whenReady(api: GrecaptchaApi): Promise<GrecaptchaApi> {
  return new Promise((resolve) => {
    api.ready(() => {
      resolve(api);
    });
  });
}

function injectScript(siteKey: string): Promise<GrecaptchaApi> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;

    script.onerror = (): void => {
      script.remove();
      reject(new Error('The reCAPTCHA script could not be loaded.'));
    };

    script.onload = (): void => {
      const api = window.grecaptcha;
      if (api === undefined) {
        script.remove();
        reject(new Error('The reCAPTCHA script loaded without exposing its API.'));
        return;
      }
      resolve(api);
    };

    document.head.appendChild(script);
  });
}

function load(siteKey: string): Promise<GrecaptchaApi> {
  // The reset lives in the rejection handler, not inside the executor: an assignment made while the
  // promise is still being constructed would be overwritten by the assignment below, which is how a
  // failed load ends up cached for the lifetime of the page.
  loader ??= injectScript(siteKey)
    .then(whenReady)
    .catch((error: unknown) => {
      loader = null;
      throw error;
    });

  return loader;
}

/**
 * Starts loading the challenge before it is needed.
 *
 * v3 scores the interaction rather than posing a puzzle, so it has to be watching while the user fills
 * the form: loading it at submit time leaves it nothing to observe and produces a near-zero score for a
 * perfectly legitimate provider. Failures are ignored here — the submit path reports them.
 */
export function preloadRecaptcha(): void {
  if (!isRecaptchaActive()) {
    return;
  }

  void load(config.recaptchaSiteKey).catch(() => undefined);
}

/**
 * Produces a single-use reCAPTCHA v3 token for an action. The token is only ever a claim: the score is
 * checked server-side by portal-backend against the SECRET key, which never reaches the browser
 * (SAD-001 P-01).
 *
 * Returns an empty string when the challenge is switched off or unconfigured, which portal-backend
 * rejects unless its own Recaptcha:Disabled flag is set — the two flags have to agree, and the portal
 * degrades to unverifiable rather than to silently unverified.
 */
export async function executeRecaptcha(action: string): Promise<string> {
  if (!isRecaptchaActive()) {
    return '';
  }

  const api = await load(config.recaptchaSiteKey);
  return api.execute(config.recaptchaSiteKey, { action });
}
